import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, InfoIcon } from 'lucide-react';
import { PageHeader } from '../components/PageHeader';
import { UnitShowcase } from '../components/UnitShowcase';
import { Reveal } from '../components/Reveal';
import { fees, properties, stayTimes } from '../data/units';
import { formatNaira } from '../utils/format';
const extras = [{
  label: 'Early check-in',
  detail: 'Arrive before 2 PM — charged only if approved.',
  price: `${formatNaira(fees.earlyLateMin)}–${formatNaira(fees.earlyLateMax)}`
}, {
  label: 'Late checkout',
  detail: 'Stay beyond 12 noon — charged only if approved.',
  price: `${formatNaira(fees.earlyLateMin)}–${formatNaira(fees.earlyLateMax)}`
}, {
  label: 'Photoshoot session',
  detail: `${fees.photoshootHours}-hour block at the property.`,
  price: formatNaira(fees.photoshootSession)
}, {
  label: 'Refundable caution fee',
  detail: 'Mandatory, collected upfront on every booking.',
  price: formatNaira(fees.cautionFee)
}];
export function Accommodations() {
  return <>
      <PageHeader eyebrow="Apartments" title="Thoughtful design, exceptional comfort" intro={`Every apartment shares the same standard — pool, generator-backed power and 24-hour internet. Check-in ${stayTimes.checkIn}, checkout ${stayTimes.checkOut}.`} data-id="element-441" data-mp="181b0lzl1htey" />

      <section className="bg-ivory-100" data-id="element-442" data-mp="1cudvqc5gtax9">
        <div className="mx-auto max-w-[1240px] space-y-24 px-5 py-20 sm:px-8 lg:space-y-32 lg:px-12 lg:py-28" data-id="element-443" data-mp="p49to97xdj8d">
          {properties.map((unit, index) => <div key={unit.id} id={unit.id} className="scroll-mt-28" data-id="element-444" data-mp="l492dnzq9djw" data-mpi={index}>
              <UnitShowcase unit={unit} reversed={index % 2 === 1} data-id="element-445" data-mp="z2pghzzx66c2" />
            </div>)}
        </div>
      </section>

      <section className="bg-ivory-200" data-id="element-446" data-mp="u1rsv7w54k36">
        <div className="mx-auto max-w-[1240px] px-5 py-20 sm:px-8 lg:px-12 lg:py-24" data-id="element-447" data-mp="2ahmcxuyfnxtg">
          <div className="grid gap-12 lg:grid-cols-[0.9fr_1.1fr] lg:gap-20" data-id="element-448" data-mp="2ajfjzbcykk1z">
            <Reveal data-id="element-449" data-mp="25i9qvcfdyurx">
              <p className="flex items-center gap-3 text-[11px] uppercase tracking-wordmark text-gold-700" data-id="element-450" data-mp="25kvy1ayd789m">
                <span className="h-px w-8 bg-gold-500" aria-hidden="true" data-id="element-451" data-mp="2hksvqkti8g2c" />
                Rates & fees
              </p>
              <h2 className="mt-4 font-display text-3xl leading-tight text-forest-700 sm:text-[2.6rem]" data-id="element-452" data-mp="2mjm7a7p8dwzv">What your stay costs</h2>
              <p className="mt-5 text-[15px] leading-relaxed text-muted" data-id="element-453" data-mp="2rigpb7fdjhix">
                Your total is accommodation plus any approved extras plus the {formatNaira(fees.cautionFee)} refundable caution fee.
                Final amounts are confirmed by our booking system when you submit.
              </p>
              <div className="mt-6 rounded-sm bg-ivory-50 p-5 text-[13px] leading-relaxed text-ink shadow-soft" data-id="element-454" data-mp="23s35u5auuqzy">
                <p className="flex items-center gap-2 font-normal text-forest-700" data-id="element-455" data-mp="28m7f4v7lfb47">
                  <InfoIcon size={15} className="text-gold-500" aria-hidden="true" data-id="element-456" data-mp="1b4gs447wy5zd" />
                  Example — 3-bedroom, 2 nights
                </p>
                <p className="mt-2 text-muted" data-id="element-457" data-mp="18ku5bb14kzvs">
                  {formatNaira(440000)} accommodation + {formatNaira(fees.cautionFee)} caution fee ={' '}
                  <span className="text-forest-700" data-id="element-458" data-mp="13j4u9vdwkm7p">{formatNaira(540000)}</span>
                </p>
              </div>
              <Link to="/booking" className="group mt-8 inline-flex items-center gap-2 rounded-full bg-forest-700 px-6 py-3 text-sm text-ivory-100 transition-colors duration-150 ease-premium hover:bg-forest-600" data-id="element-459" data-mp="tn1tasusetmc">
                Start a booking
                <ArrowRightIcon size={15} className="transition-transform duration-200 ease-premium group-hover:translate-x-1" aria-hidden="true" data-id="element-460" data-mp="ksvfzn4ud3zn" />
              </Link>
            </Reveal>

            <Reveal delay={0.06} data-id="element-461" data-mp="aufqtppdk4ss">
              <ul className="divide-y divide-forest-700/10 border-y border-forest-700/10" data-id="element-462" data-mp="5xz1wtacpmen">
                {properties.map(unit => <li key={unit.id} className="flex items-baseline justify-between gap-6 py-5" data-id="element-463" data-mp="1x5gq7oamlpg">
                    <div data-id="element-464" data-mp="qnt6fcqgkm8m">
                      <p className="text-[15px] text-ink" data-id="element-465" data-mp="qol36qvskq0h">{unit.name}</p>
                      <p className="mt-1 text-[13px] text-muted" data-id="element-466" data-mp="vk9ygi0s3kqz">Max {unit.maxGuests} guests · per night</p>
                    </div>
                    <p className="shrink-0 font-display text-2xl text-forest-700" data-id="element-467" data-mp="hsdhna4k8x9w">{formatNaira(unit.nightlyRate)}</p>
                  </li>)}
                {extras.map(extra => <li key={extra.label} className="flex items-baseline justify-between gap-6 py-5" data-id="element-468" data-mp="7yd4ckz4klq3">
                    <div data-id="element-469" data-mp="csqvgb6r86oy">
                      <p className="text-[15px] text-ink" data-id="element-470" data-mp="3v18437h4exye">{extra.label}</p>
                      <p className="mt-1 text-[13px] text-muted" data-id="element-471" data-mp="2wxk2v4ruj8r">{extra.detail}</p>
                    </div>
                    <p className="shrink-0 text-right text-[15px] text-gold-700" data-id="element-472" data-mp="1t9d6twvm2vzp">{extra.price}</p>
                  </li>)}
              </ul>
            </Reveal>
          </div>
        </div>
      </section>
    </>;
}